import {
  BenefitDocumentDto,
  BenefitDocumentType,
  EmployerDocumentDto,
} from '@zorro/clients';
import { ERROR_MESSAGES } from '@zorro/common-utils';
import { Stack } from '@zorro/web/design-system';
import {
  createBenefitDocument,
  showErrorNotification,
  useLoadingOverlay,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useState } from 'react';

import {
  BenefitDocumentCard,
  settleFileUploads,
  useIchraLetterDocument,
} from '../../../../BenefitDocuments';
import {
  DocumentDeleteModal,
  DocumentListSection,
} from '../../../../DocumentListSection';
import { toUniqueFileNames } from '../../../../EmployerDocuments';
import {
  getExistingFileNames,
  getGroupedBenefitDocuments,
} from './EmployeePageFilesUtils';

type Props = {
  onboardingPeriodId: string;
  employeeId: string;
  employerId: string;
  isDon: boolean;
};

/**
 * The back-office Files tab on the employee page, for the admin and ops
 * portals. The employee sees the same documents through
 * `EnrollmentFileTab` in the employee domain.
 */
export const EmployeePageFiles = ({
  onboardingPeriodId,
  employeeId,
  employerId,
  isDon,
}: Props) => {
  const [isDeleteDocumentsModalOpen, setIsDeleteDocumentsModalOpen] =
    useState(false);
  const [selectedDocument, setSelectedDocument] = useState<
    BenefitDocumentDto | EmployerDocumentDto
  >();

  const { startLoading, stopLoading } = useLoadingOverlay();
  const { data: benefitDocuments } = useMonolithQuery({
    method: 'benefitDocumentsControllerGetBenefitDocumentsByOnboardingPeriod',
    params: [onboardingPeriodId],
  });
  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });
  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });

  const { mutate: createBenefitDocumentMutation } = useMonolithMutation({
    method: 'benefitDocumentsControllerCreateBenefitDocument',
    successMessage: 'Document uploaded',
  });

  const { mutate: removeBenefitDocumentMutation } = useMonolithMutation({
    method: 'benefitDocumentsControllerRemoveBenefitDocument',
    successMessage: 'Document deleted',
  });

  const { ichraLetterDocument, downloadIchraLetter } = useIchraLetterDocument({
    employee,
    onboardingPeriod,
  });

  // Scoped to the whole tab, not per section — the admin/employee toggle moves
  // documents between the two, so a per-section check could be walked around.
  const existingFileNames = getExistingFileNames(
    benefitDocuments,
    ichraLetterDocument
  );

  const uploadBenefitDocument = async (
    files: File[] | null,
    isSharedWithEmployee: boolean
  ) => {
    if (!files || files.length === 0) {
      showErrorNotification({
        message: ERROR_MESSAGES.NO_FILE_SELECTED_ERROR_MESSAGE,
      });
      return;
    }

    startLoading();
    try {
      // A drop is no place to ask, so a taken name gains a ' (n)'.
      const uniqueFileNames = toUniqueFileNames(
        files.map((file) => file.name),
        existingFileNames
      );

      const { failed } = await settleFileUploads(files, async (file, index) => {
        const document = await createBenefitDocument(
          employeeId,
          employerId,
          file,
          onboardingPeriodId,
          isSharedWithEmployee,
          BenefitDocumentType.OTHER
        );

        await createBenefitDocumentMutation([
          { ...document, fileName: uniqueFileNames[index] },
        ]);
      });

      failed.forEach(({ file, error }) => {
        showErrorNotification({ message: error.message, title: file.name });
      });
    } finally {
      stopLoading();
    }
  };

  const handleDeleteBenefitDocument = (
    document: BenefitDocumentDto | EmployerDocumentDto
  ) => {
    setSelectedDocument(document);
    setIsDeleteDocumentsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteDocumentsModalOpen(false);
    setSelectedDocument(undefined);
  };

  // The card shows delete on the handler alone, no longer on canEdit, so the
  // read-only persona withholds it rather than relying on that flag.
  const onDelete = isDon ? undefined : handleDeleteBenefitDocument;

  const { adminDocuments, planDocuments } =
    getGroupedBenefitDocuments(benefitDocuments);

  const employeeFullName = employee
    ? `${employee.firstName} ${employee.lastName}`
    : undefined;

  const hasAdminDocuments = adminDocuments.length > 0;
  const hasPlanDocuments =
    Boolean(ichraLetterDocument) || planDocuments.length > 0;

  return (
    <Stack gap="xxl">
      <DocumentListSection
        readOnlyEmptySubtitle="Enrollment documents will be uploaded here"
        title="Admin documents"
        subtitle={`(not shared with ${employeeFullName})`}
        canManage={!isDon}
        isEmpty={!hasAdminDocuments}
        isMultiple
        onUpload={(files) => uploadBenefitDocument(files, false)}
        uploadLabel="Upload admin document"
      >
        {adminDocuments.map((adminDocument) => (
          <BenefitDocumentCard
            key={adminDocument.id}
            document={adminDocument}
            canEdit={!isDon}
            onDelete={onDelete}
            existingFileNames={existingFileNames}
          />
        ))}
      </DocumentListSection>

      <DocumentListSection
        readOnlyEmptySubtitle="Enrollment documents will be uploaded here"
        title="Employee documents"
        subtitle={`(shared with ${employeeFullName})`}
        canManage={!isDon}
        isEmpty={!hasPlanDocuments}
        isMultiple
        onUpload={(files) => uploadBenefitDocument(files, true)}
        uploadLabel="Upload employee document"
      >
        {ichraLetterDocument && (
          <BenefitDocumentCard
            document={ichraLetterDocument}
            canEdit={!isDon}
            onDownload={downloadIchraLetter}
          />
        )}
        {planDocuments.map((planDocument) => (
          <BenefitDocumentCard
            key={planDocument.id}
            document={planDocument}
            canEdit={!isDon}
            onDelete={onDelete}
            existingFileNames={existingFileNames}
          />
        ))}
      </DocumentListSection>

      <DocumentDeleteModal
        isOpen={isDeleteDocumentsModalOpen}
        fileName={selectedDocument?.fileName}
        onConfirm={async () => {
          if (selectedDocument) {
            await removeBenefitDocumentMutation([selectedDocument.id]);
          }
          closeDeleteModal();
        }}
        onClose={closeDeleteModal}
      />
    </Stack>
  );
};
